import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X, ChevronDown, Globe } from 'lucide-react';
import { Button } from './ui/button';

export const Navbar = () => {
  const { t, language, setLanguage, languages } = useLanguage();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/#top10', label: t('nav.top10') },
    { path: '/#sovereign-nodes', label: t('nav.sovereignNodes') },
    { path: '/hackathons', label: t('hackathons.title') },
    { path: '/grants', label: 'Grants' },
    { path: '/leaderboard', label: 'Leaderboard' },
    { path: '/apply', label: t('nav.apply') },
    { path: '/dashboard', label: t('nav.dashboard') },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path.split('#')[0]);
  };

  const currentLang = languages.find(l => l.code === language);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-md border-b border-[#1a4138]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-black font-extrabold text-base tracking-tight">WR</span>
            </div>
            <span className="text-white font-semibold text-lg hidden sm:block">We·R2 Builders</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-5">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link text-sm font-medium ${
                  isActive(link.path) ? 'text-white' : 'text-slate-400'
                }`}
                data-testid={`nav-${link.path.replace(/[/#]/g, '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side - Language + Admin */}
          <div className="flex items-center gap-4">
            {/* Language Switcher */}
            <div className="relative lang-switcher">
              <button
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
                data-testid="language-switcher"
              >
                <Globe className="w-4 h-4" />
                <span className="hidden sm:inline text-sm">{currentLang?.name}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${langMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {langMenuOpen && (
                <div className="lang-dropdown">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        setLanguage(lang.code);
                        setLangMenuOpen(false);
                      }}
                      className={`lang-option w-full text-left flex items-center gap-2 ${
                        language === lang.code ? 'active' : ''
                      }`}
                      data-testid={`lang-${lang.code}`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Admin Link */}
            <Link to="/admin" data-testid="admin-link">
              <Button variant="outline" size="sm" className="hidden sm:flex">
                {t('nav.admin')}
              </Button>
            </Link>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-white p-2"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden mobile-menu border-t border-[#1a4138]">
          <div className="px-4 py-4 space-y-3">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`block py-2 text-base font-medium ${
                  isActive(link.path) ? 'text-white' : 'text-slate-400'
                }`}
                data-testid={`mobile-nav-${link.path.replace(/[/#]/g, '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/admin"
              onClick={() => setMobileMenuOpen(false)}
              className="block py-2 text-base font-medium text-slate-400"
              data-testid="mobile-nav-admin"
            >
              {t('nav.admin')}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};
